import React from 'react'
import SEOTitle from '@chegg-wt/core/components/SEOTitle'
import { useRouter } from 'next/router'
import useRedirect from '@chegg-wt/core/hooks/useRedirect'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import useUserRole from '@chegg-wt/core/hooks/useUserRole'
import SourceTypeSelection from '@chegg-wt/core/components/Layouts/SourceTypeSelection'
import { NextPageWithLayout } from '@chegg-wt/core/types/layout'
import { getLvpLayout } from '@chegg-wt/core/components/Layouts/LargeVideoPlayerLayout'

import { NextPageContext } from 'next'
import {
  getActualStyleId,
  getCapitalizedSEONameOfStyle,
  isStyleIdInValid,
} from '@chegg-wt/core/utils/styles'
import { UserRoles } from '@chegg-wt/core/constants/user'
import MetaDescription from '@chegg-wt/core/components/Head/MetaDescription'
import SourceTypeSelectionTop from '@chegg-wt/core/components/SourceTypeSelectionTop'
import { getUrlStyleNameV2 } from '@chegg-wt/core/api/oneGraph'
import { useOnUnloadTimeOnPage } from '@chegg/time-on-page'
import { useShowDiscountModal } from '@chegg-wt/core/hooks/useShowDiscountModal'
import { flankerDesignUpgradeCopy } from '../../copy/upgrade'
import considerCredibility from '../../copy/credibility'
import { sharedResources } from '../../copy/resources'

interface SourceSelectionPageProps {
  initialUrlStyleName: string
}

const SourceSelectionPage: NextPageWithLayout<SourceSelectionPageProps> = ({
  initialUrlStyleName,
}) => {
  useShowDiscountModal()
  const { style } = useRouter().query as { style: string }
  const userRole = useUserRole()
  const { toDashboard } = useRoutesContext()
  useRedirect({
    shouldRedirect: userRole === UserRoles.PRO_USER,
    redirectRoute: toDashboard(),
  })
  const formattedStyle = getCapitalizedSEONameOfStyle(style)
  useOnUnloadTimeOnPage()

  return (
    <>
      <SEOTitle
        title={`EasyBib®: Free ${formattedStyle} Citation & Bibliography Generator`}
      />
      {style === 'mla' && (
        <MetaDescription content="Generate accurate MLA citations with EasyBib. Automatically create your works cited page and format your bibliography." />
      )}
      <SourceTypeSelection
        copy={{
          upgradeCopy: flankerDesignUpgradeCopy,
          considerCredibility,
          resources: sharedResources,
        }}
        styleId={getActualStyleId(style)}
        initialUrlStyleName={initialUrlStyleName}
      />
    </>
  )
}

export const getServerSideProps = async ({ query }: NextPageContext) => {
  const { style } = query as { style: string }

  const [initialUrlStyleName] = await getUrlStyleNameV2(style)
  return {
    props: {
      initialUrlStyleName,
      isDefaultStyle: false,
    },
    notFound: isStyleIdInValid(style),
  }
}

SourceSelectionPage.getPageLayout = (page, ssrProps) =>
  getLvpLayout(page, () => <SourceTypeSelectionTop {...ssrProps} />)

export default SourceSelectionPage
